import { BookingStatusType } from '../components/StatusBadge';
import { DerivedPaymentStatusType } from './booking/bookingApiSlice';
export type IssueReportStatus = 'open' | 'in progress' | 'on hold' | 'done';
export interface Asset {
    id: string;
    name: string;
    accessories: string[];
}

export interface CustomTheme {
    palette: {
        background: {
            appDark: string;
        };
    };
}
export interface ProfileInfo {
    name: string;
    sub: string;
    nickname: string;
    email: string;
    picture: string;
}

export interface Rental {
    id: string;
    orderId: string;
    orderItemId: string;
    token: string;
    isActive: boolean;
    doors: Array<{
        door: number;
        depotId: string;
    }>;
    startedDate: Date | null;
    expiryDate?: Date;
    finishedAtDate?: Date;
    finishedBy?: string;
    finishedByEmail?: string;
    lastLockerOpened?: string | null;
    lastLockerClosed?: string | null;
    metadata: any;
}

export interface OrderItem {
    orderId?: string;
    id: string;
    groupId: string;
    amount: number;
    isFullDay: Boolean;
    fromDate: Date;
    toDate: Date;
    startDate?: Date;
    endDate?: Date;
    rental?: Rental;
    depot?: Depot;
    doors?: DepotDoor[];
    Group?: {
        id: string;
        name: string;
    };
    status: BookingStatusType;
}

export interface ContactData {
    firstname?: string;
    lastname?: string;
    email: string;
    telephone?: string;
    company?: string;
}

export interface LocationAddress {
    street: string;
    city: string;
    postalCode: string;
    geo_lat?: number;
    geo_lng?: number;
}

export interface Booking {
    id: string;
    reservationId: string;
    locationId: string;
    bookingNumber: string;
    /**
     * The booking's problem reports, attached server-side by
     * `damageReportBelongsToBooking` — the same rule and the same field the
     * bookings list renders its "Problem" yes/no column from. Optional because
     * it arrives with the row: `undefined` means the booking has not loaded,
     * NOT that there are no reports.
     */
    damageReports?: { id: string }[];
    items: OrderItem[];
    createdDate: Date;
    contactData?: ContactData;
    recipientData?: ContactData;
    clientMessage?: string;
    comment?: string;
    createdBy: 'user' | 'admin';
    useCase?: string;
    locationAddress?: LocationAddress;
    lockerSize?: string;
    isGuestBooking?: boolean;
    rentalStartDateTime: string;
    rentalEndDateTime: string;
    lockerLastOpenTime?: string;
    createdAt: string;
    updatedAt: string;
    reservation: {
        metadata: any;
    };
}

export type NotificationType = 'success' | 'warning' | 'error' | 'info';
export type ContainerType = 'kolula12' | 'db4';

export interface DepotDoor {
    door: number;
    assetId: string;
}

export interface Depot {
    id: string;
    name: string;
    description: string;
    containerType: ContainerType;
    enabled: boolean;
    status: {
        bookable: boolean;
        enabled?: boolean;
    };
    locationId: string;
    thingsId: string;
    last_seen: Date | null;
    doorMapping: DepotDoor[];
    doorCount: number;
    isFictional: boolean;
}

export interface FullMappedDoor {
    door: number;
    /** Null at a station with no lock controller: there is no lock to report. */
    doorState: 'open' | 'closed' | null;
    assetId: string;
    assetState: 'available' | 'blocked' | 'inUse' | 'soonAvailable';
    assetName: string;
    assetAccessories: string[];
    orderId?: string;
    bookingNumber?: string;
}

export interface FullMappedDepot extends Depot {
    doorMapping: FullMappedDoor[];
}

export interface IssueReportComment {
    /** `creation` marks the entry written with the report itself (it carries
     *  the reporter's `profileInfo`); `booking_deleted` the note written when
     *  the report's booking was hard-deleted. Real comments carry none. */
    type?: string;
    /** Set on a `booking_deleted` entry: the number the booking had. */
    booking_number?: string | null;
    message?: string;
    created_at?: string;
    profileInfo?: ProfileInfo;
}

// SUP equipment check (#1604): customer-flow lifecycle of a report filed from
// the equipment-check screen. Distinct from the admin workflow `state`.
// null = report predates the lifecycle or was not filed via the check screen.
export type IssueReportCustomerStatus = 'open' | 'confirmed' | 'withdrawn';

// Equipment item flagged on the check screen; `key` matches the booked
// product's equipment configuration (Group.equipment).
export interface IssueReportItem {
    key: string;
    issueKind: 'missing' | 'damaged';
    /** The product's label for the item ({ de, en, … }), attached by the list
     *  endpoint from Group.equipment; null when no longer configured. */
    label_i18n?: Record<string, string> | null;
}

export interface IssueReport {
    id: string;
    doorSize: string;
    /** Locker the report concerns — null when it concerns none (a payment
     *  issue, an app problem, a location without a station). */
    door: number | null;
    state: IssueReportStatus;
    status?: IssueReportCustomerStatus | null;
    items?: IssueReportItem[] | null;
    message: string;
    comments: IssueReportComment[] | null;
    address: string;
    depotId: string;
    locationId: string;
    issueType: string;
    orderId: string;
    bookingNumber: string;
    createdBy: string;
    createdAt: string;
    profileInfo: ProfileInfo;
    service: string;
    userCreatorEmail: string;
    userCreatorName: string;
    archivedAt?: string | null;
    archivedBy?: string | null;
    archiveReason?: string | null;
    archiveNote?: string | null;
}

export interface DetailedIssueReport {
    id: string;
    message: string;
    orderId: string | undefined;
    state: 'open' | 'in progress' | 'on hold' | 'done';
    createdBy: string;
    createdAt: string;
    door: number;
    assets: {
        depotId: string;
        door: number;
        depot: Depot;
        location: string;
    }[];
}

// export type DamageReportState = 'open' | 'in progress' | 'on hold' | 'done';

export interface Location {
    id: string;
    enabled: boolean;
    name: string;
    address: {
        street: string;
        postalCode: string;
        city: string;
    };
    status: {
        bookable: boolean;
        notification?: {
            type: NotificationType;
            text: string;
        };
    };
    services: string[];
    assetGroups?: AssetGroup[];
    /**
     * #3219: the location's IANA zone (`location.time_zone`). Optional so an
     * older server reads as the station default — resolve it with
     * `selectLocationTimeZone` / `useLocationTimeZone`, never inline.
     */
    timeZone?: string;
}

export interface Cabinet {
    id: string;
    enabled: boolean;
    name: string;
    address: {
        street: string;
        postalCode: string;
        city: string;
    };
    status: {
        bookable: boolean;
        notification?: {
            type: NotificationType;
            text: string;
        };
    };
}

export interface SingleDoorStatus {
    door: number;
    /** Null at a station with no lock controller: there is no lock to report. */
    doorState: 'open' | 'closed' | null;
    assetId: string;
    assetState: 'available' | 'blocked' | 'inUse' | 'soonAvailable';
    orderId?: string | null;
    bookingNumber?: string | null;
}

export interface DoorStatus {
    doors: SingleDoorStatus[];
}

export interface Thing {
    id: string;
    name: string;
    lastSeenDate: Date | null;
    minutesFromLastSeen: number | undefined;
    remoteItState: string;
}

export interface RebookCustomerDoorResult {
    oldDoor: number;
    newDoor: number;
}

export interface DailyBookingStat {
    date: Date;
    orderCount: number;
    resourceCount: number;
    total: number;
}

export interface LocationBookingStats {
    daily: DailyBookingStat[];
}

/**
 * Tenant identifier used for routing (X-Tenant header), Redux state, and
 * dynamic config imports. Self-service tenants pick their own slug at
 * registration so this can no longer be a fixed union — it's any
 * lowercase-alphanumeric-and-hyphens string. The literals below are the
 * historically-known legacy slugs, kept for autocomplete and as a quick
 * reference of which tenants pre-date self-service.
 */
export type LegacyTenantSlug =
    | 'kolula'
    | 'vonovia'
    | 'vonovia_v2'
    | 'segmenta'
    | 'dhu'
    | 'seezeit'
    | 'strandbad-pertisau'
    | 'badesee-petershagen'
    | 'kolula-key'
    | 'segmenta_v2';

export const LEGACY_TENANT_SLUGS: ReadonlySet<string> = new Set<LegacyTenantSlug>([
    'kolula',
    'vonovia',
    'vonovia_v2',
    'segmenta',
    'dhu',
    'seezeit',
    'strandbad-pertisau',
    'badesee-petershagen',
    'kolula-key',
    'segmenta_v2',
]);

/**
 * The retired v1 spellings of two tenants (CEO, 2026-09-18).
 *
 * `vonovia` and `segmenta` WITHOUT the suffix were the interim app's slugs.
 * They name the same tenant and the same database as their `_v2` counterparts —
 * the `X-Tenant` header is normalised to the unsuffixed form either way — but
 * they used to route the request to a **different, stale backend**
 * (`config.api.oldBaseUrl`, "missing fields like `status.bookable`"). Everyone
 * has been migrated to the current app, so that route is now closed: no slug
 * here is offered in the switcher, and nothing resolves a base URL from it.
 *
 * **They deliberately stay in `LEGACY_TENANT_SLUGS` above.** That set answers a
 * different question — *which store does this tenant live in* — and it is read
 * by roughly a dozen `isSelfServiceTenant` checks. Removing them there would
 * make a stale saved slug classify as **self-service**, which shows the wrong
 * sidebar, exposes the Customers area and asks the backend for
 * `tenant.entitlements` it will answer 409 to.
 */
export const DEPRECATED_TENANT_SLUGS: ReadonlySet<string> = new Set<string>([
    'vonovia',
    'segmenta',
]);

/**
 * The slug to actually use for a tenant the user may still have stored or
 * granted under its retired v1 spelling.
 *
 * Applied wherever a slug arrives from outside this render — `localStorage`, a
 * JWT permission — so a user who last selected `vonovia` lands on `vonovia_v2`
 * rather than on a tenant the switcher no longer offers.
 */
export const canonicalTenantSlug = (slug: string): string =>
    DEPRECATED_TENANT_SLUGS.has(slug) ? `${slug}_v2` : slug;

// `(string & {})` keeps the LegacyTenantSlug literals autocompletable in
// TypeScript while still accepting any string at runtime.
export type Tenant = LegacyTenantSlug | (string & {});

export interface UpdateDepotData {
    id: string;
    enabled: boolean;
}

export interface UpdateLocationData {
    id: string;
    enabled: boolean;
    status: {
        bookable: boolean;
        notification?: {
            type: NotificationType;
            text: string;
        };
    };
}

export interface ApartmentAssets {
    name: string;
    address: string;
    id: string;
    compartmentTypes: string[];
    // A room's titles; null for a station's object, which is labelled by
    // its own name (asset/for-issue-report).
    langNames?: {
        de: {
            title: string;
            description: string;
        };
        en: {
            title: string;
            description: string;
        };
    } | null;
}

// A duration's availability. The storage payload carries a plain count; the
// rental/SUP payloads mirror the end-user shape and carry { count, price }
// (apps/end-user/src/types/assetGroup.ts). Read counts via
// `getAvailabilityCount` (utils) so both shapes work.
export type AvailabilitySlotValue = number | { count: number, price?: number };
export type AvailabilitySlot = Record<DurationId, AvailabilitySlotValue>;
export type AssetGroupAvailability = Record<AvailabilityDate, Record<AvailabilityTime, AvailabilitySlot>>;
type DurationId = string; /* optionId (identifies board type and booking duration) */
type AvailabilityDate = string; /* YYYY-MM-DD */
type AvailabilityTime = string; /* HH:ii */

export interface AssetGroup {
    id: string;
    name: string;
    locationId: string;
    description: string;
    availabilities: AssetGroupAvailability;
    isFullDay: boolean;
}

export interface ReservationItem {
    groupId: string;
    amount: number;
    price: number;
    fromDate: string;
    toDate: string;
}

export interface Reservation {
    reservationItems: Array<ReservationItem>;
    // createdDate: Date;
    time_to_live: number;
    locationId: string;
    payment: {
        currency: string;
        state: string; // TODO: use constants
        payment_method: string; // TODO: use constants
        totalPrice: number;
    };
    reservationId: string;
    bookingNumber: string;
    discount?: null | {
        code: string;
        amount: number;
    };
}

export interface CreateNewReservationData {
    groupId: string;
    amount: number;
    fromDate: string;
    toDate: string;
    isFullDay: boolean;
}

export interface CreateNewOrderData {
    groupId: string;
    fromDate: string;
    toDate: string;
    amount: number;
    comment: string;
    isGuestBooking?: boolean;
    contactData: ContactData;
}

export interface PricingDiscount {
    nights: [number, number];
    discount: number;
}

export interface OrderForAssetAvailability {
    orderId: string;
    bookingNumber: string;
    service: string;
    startDate: string;
    endDate: string;
    // A read field: it can carry a derived money-return status, which is
    // not one an operator may set. See DerivedPaymentStatusType.
    paymentStatus: DerivedPaymentStatusType;
    status: BookingStatusType;
    reasonForBlocking?: string | null;
}

export interface AssetAvailability {
    assetId: string;
    assetName: string;
    locationId: string;
    locationName: string;
    /**
     * Station the object sits at. Nullable because the schema allows it; in
     * practice every object has one, apartments and venues included.
     */
    stationId: string | null;
    stationName: string | null;
    /**
     * The product this object fulfils — today `Group`, after the W1.2b cutover
     * (#1795) `Product`. The contract name does not change with the source.
     */
    productId: string | null;
    productName: string | null;
    doorNumbers: number[];
    useCases: string[];
    orders: OrderForAssetAvailability[];
}

export interface Group {
    id: string;
    name: string;
    langNames: {
        de: { title: string, description: string };
        en: { title: string, description: string };
    };
    priceCategory: string;
    locationId: string;
    compartmentTypes: string[];
    description: string;
    isFullDay: boolean;
    limit: number;
    priceCategoryInfo: {
        id: string;
        name: string;
        priceScale: Array<{
            isFullDay: boolean;
            durationInMinutes: number;
            days: number | null;
            startTime: string | null;
            endTime: string | null;
            price: number;
        }>;
    };
    featuredImage: string | null;
}

export interface GroupsWithPricesResponse {
    groups: Group[];
}

export interface GroupAvailability {
    duration: number; // in days (1, 3, 7)
    price: number;
    available: boolean;
}

export interface GroupAvailabilitiesResponse {
    groupId: string;
    availabilities: GroupAvailability[];
}
